/**
 * `MonoLink` / `Dash` — tiny shared primitives used across the readiness
 * cards (headers, section titles, kickers, "— not measured" fallbacks).
 */

import type { CSSProperties, MouseEventHandler, ReactNode } from 'react';

import { useC } from '../../lib/theme.js';
import { monoFont } from './fonts.js';

export interface KickerProps { children: ReactNode }

export function Kicker({ children }: KickerProps): JSX.Element {
  const C = useC();
  return (
    <span
      style={{
        fontFamily: monoFont,
        fontSize: 11,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: C.muted,
      }}
    >
      {children}
    </span>
  );
}

export interface MonoLinkProps {
  readonly onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  readonly href?: string | undefined;
  readonly children: ReactNode;
  readonly style?: CSSProperties | undefined;
  readonly 'aria-label'?: string | undefined;
}

/** A keyboard-focusable, underline-on-hover inline anchor — used for the
 *  rescan cadence pop and "See all activity" lines in the mock. */
export function MonoLink({ onClick, href, children, style, 'aria-label': ariaLabel }: MonoLinkProps): JSX.Element {
  const C = useC();
  const base: CSSProperties = {
    fontFamily: monoFont,
    fontSize: 12,
    color: C.text,
    textDecoration: 'underline',
    textDecorationColor: C.dim,
    textUnderlineOffset: '3px',
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    padding: 0,
    ...style,
  };
  if (href !== undefined) {
    return (
      <a href={href} style={base} aria-label={ariaLabel}>{children}</a>
    );
  }
  return (
    <button type="button" onClick={onClick} style={base} aria-label={ariaLabel}>{children}</button>
  );
}

/** The muted em-dash we use everywhere there is "no data yet". */
export function Dash(): JSX.Element {
  const C = useC();
  return <span aria-hidden="true" style={{ color: C.dim }}>—</span>;
}
